import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { Toast, ToastProvider as ConcordToastProvider, ToastViewport } from "@lendcord/concord";

type ToastStatus = "neutral" | "success" | "warning" | "error";

interface ToastInput {
  status?: ToastStatus;
  title: string;
  description?: string;
}

interface ToastItem extends ToastInput {
  id: number;
}

interface ToastContextValue {
  showToast: (toast: ToastInput) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

let nextToastId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showToast = useCallback((toast: ToastInput) => {
    setToasts((prev) => [...prev, { ...toast, id: nextToastId++ }]);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      <ConcordToastProvider>
        {children}
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            status={toast.status}
            title={toast.title}
            description={toast.description}
            defaultOpen
            onOpenChange={(open) => {
              if (!open) dismissToast(toast.id);
            }}
          />
        ))}
        <ToastViewport />
      </ConcordToastProvider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
